import type { ApprovalStatus, CustodyCase, DocStatus, HistoryEntry, HistoryKey, LetterStatus, PaymentStatus, VehicleCategory } from '../types/case';
import type { AppConfig } from '../types/config';

export type Tone = 'navy' | 'green' | 'warning' | 'danger' | 'gray' | 'sky';
export interface Labeled { label: string; tone: Tone; }

export const isPaid = (c: CustodyCase) => c.paymentStatus === 'paid' || c.paymentStatus === 'reconciled';
export const isActiveInventory = (c: CustodyCase) => c.stage === 'at_station';

export function stageInfo(c: CustodyCase): Labeled {
  if (c.stage === 'released') return { label: 'অবমুক্ত ও হস্তান্তর সম্পন্ন', tone: 'green' };
  if (c.letter?.status === 'issued') return { label: 'অবমুক্তির অনুমোদনপ্রাপ্ত', tone: 'green' };
  if (c.letter?.status === 'signed') return { label: 'পত্র ইস্যু অপেক্ষমাণ', tone: 'navy' };
  if (c.approval === 'approved') return { label: 'স্বাক্ষর অপেক্ষমাণ', tone: 'navy' };
  if (c.approval === 'pending') return { label: 'অনুমোদন অপেক্ষমাণ', tone: 'warning' };
  if (c.approval === 'returned') return { label: 'নথি সংশোধনের জন্য ফেরত', tone: 'danger' };
  if (c.approval === 'rejected') return { label: 'অনুমোদন প্রত্যাখ্যাত', tone: 'danger' };
  if (c.approval === 'held') return { label: 'নথি স্থগিত', tone: 'warning' };
  if (c.approval === 'drafting') return { label: 'নোটশিট প্রস্তুতি', tone: 'navy' };
  if (c.stage === 'registered') return { label: 'পিকআপ অপেক্ষমাণ', tone: 'gray' };
  if (c.stage === 'in_transit') return { label: 'পরিবহনাধীন', tone: 'sky' };
  if (c.docStatus === 'correction') return { label: 'কাগজপত্র সংশোধন প্রয়োজন', tone: 'danger' };
  if (c.docStatus === 'submitted' || c.docStatus === 'verifying') return { label: 'যাচাই অপেক্ষমাণ', tone: 'warning' };
  if (c.docStatus === 'verified' && !isPaid(c)) return { label: 'পেমেন্ট অপেক্ষমাণ', tone: 'warning' };
  if (c.docStatus === 'verified') return { label: 'যাচাই অপেক্ষমাণ', tone: 'warning' };
  return c.ownerLinked ? { label: 'কাগজপত্র অপেক্ষমাণ', tone: 'warning' } : { label: 'ডাম্পিং স্টেশনে রয়েছে', tone: 'navy' };
}

export const PAYMENT_LABEL: Record<PaymentStatus, Labeled> = {
  unpaid: { label: 'অপরিশোধিত', tone: 'gray' },
  processing: { label: 'পেমেন্ট প্রক্রিয়াধীন', tone: 'sky' },
  paid: { label: 'পরিশোধিত', tone: 'green' },
  pending_verify: { label: 'যাচাই অপেক্ষমাণ', tone: 'warning' },
  reconciled: { label: 'সমন্বিত', tone: 'green' },
  failed: { label: 'পেমেন্ট ব্যর্থ', tone: 'danger' },
  correction: { label: 'সংশোধন প্রয়োজন', tone: 'danger' },
};

export const DOC_LABEL: Record<DocStatus, Labeled> = {
  none: { label: 'জমা হয়নি', tone: 'gray' },
  submitted: { label: 'জমা দেওয়া হয়েছে', tone: 'sky' },
  verifying: { label: 'যাচাই চলছে', tone: 'warning' },
  correction: { label: 'সংশোধন প্রয়োজন', tone: 'danger' },
  verified: { label: 'যাচাইকৃত', tone: 'green' },
};

export const APPROVAL_LABEL: Record<ApprovalStatus, Labeled> = {
  none: { label: 'শুরু হয়নি', tone: 'gray' },
  drafting: { label: 'নোটশিট প্রস্তুতি', tone: 'navy' },
  pending: { label: 'অনুমোদন অপেক্ষমাণ', tone: 'warning' },
  approved: { label: 'অনুমোদিত', tone: 'green' },
  returned: { label: 'ফেরত', tone: 'danger' },
  rejected: { label: 'প্রত্যাখ্যাত', tone: 'danger' },
  held: { label: 'স্থগিত', tone: 'warning' },
};

export const LETTER_LABEL: Record<LetterStatus, Labeled> = {
  draft: { label: 'খসড়া', tone: 'gray' },
  signed: { label: 'স্বাক্ষরিত — ইস্যু অপেক্ষমাণ', tone: 'navy' },
  issued: { label: 'ইস্যুকৃত — QR সক্রিয়', tone: 'green' },
  redeemed: { label: 'ব্যবহৃত — হস্তান্তর সম্পন্ন', tone: 'gray' },
  cancelled: { label: 'বাতিল', tone: 'danger' },
};

export function categoryLabel(key: VehicleCategory, config: AppConfig): string {
  return config.categories.find((c) => c.key === key)?.label ?? key;
}

export type StepState = 'done' | 'current' | 'issue' | 'pending';
export interface ProcessStep { label: string; state: StepState; }

export function processSteps(c: CustodyCase): ProcessStep[] {
  const raw: { label: string; done: boolean; issue?: boolean }[] = [
    { label: 'হেফাজত', done: true },
    { label: 'স্টেশনে গ্রহণ', done: !!c.station },
    { label: 'কাগজপত্র', done: ['submitted', 'verifying', 'verified'].includes(c.docStatus), issue: c.docStatus === 'correction' },
    { label: 'পেমেন্ট', done: isPaid(c), issue: c.paymentStatus === 'failed' || c.paymentStatus === 'correction' },
    { label: 'যাচাই', done: !!c.verification },
    { label: 'অনুমোদন', done: c.approval === 'approved', issue: c.approval === 'returned' || c.approval === 'rejected' },
    { label: 'অবমুক্তি', done: c.stage === 'released' },
  ];
  let currentSet = false;
  return raw.map((s) => {
    if (s.done) return { label: s.label, state: 'done' };
    if (s.issue) { currentSet = true; return { label: s.label, state: 'issue' }; }
    if (!currentSet) { currentSet = true; return { label: s.label, state: 'current' }; }
    return { label: s.label, state: 'pending' };
  });
}

const TIMELINE: { label: string; keys: HistoryKey[]; office: string }[] = [
  { label: 'যানবাহন হেফাজতে নেওয়া হয়েছে', keys: ['created'], office: 'সংশ্লিষ্ট ট্রাফিক বিভাগ' },
  { label: 'ডাম্পিং স্টেশনে গ্রহণ করা হয়েছে', keys: ['received'], office: 'ডাম্পিং স্টেশন' },
  { label: 'কাগজপত্র জমা হয়েছে', keys: ['docs_submitted', 'office_docs'], office: 'যানবাহন মালিক / বিভাগীয় অফিস' },
  { label: 'জরিমানা পরিশোধ নিশ্চিত হয়েছে', keys: ['paid', 'office_payment'], office: 'হিসাব শাখা' },
  { label: 'কাগজপত্র যাচাই সম্পন্ন', keys: ['verified'], office: 'যাচাইকারী কর্মকর্তা' },
  { label: 'অনুমোদনের জন্য প্রেরিত', keys: ['forwarded'], office: 'নথি প্রস্তুতকারী কর্মকর্তা' },
  { label: 'ঊর্ধ্বতন কর্তৃপক্ষের অনুমোদন', keys: ['approved'], office: 'বিভাগীয় অনুমোদনকারী' },
  { label: 'অবমুক্তি পত্র প্রস্তুত', keys: ['issued'], office: 'অনুমোদিত স্বাক্ষরকারী' },
  { label: 'যানবাহন হস্তান্তর সম্পন্ন', keys: ['released'], office: 'ডাম্পিং স্টেশন' },
];

export function timeline(c: CustodyCase): { label: string; office: string; entry?: HistoryEntry }[] {
  return TIMELINE.map((t) => {
    const entries = c.history.filter((h) => t.keys.includes(h.key));
    return { label: t.label, office: t.office, entry: entries[entries.length - 1] };
  });
}

export function needsVerification(c: CustodyCase): boolean {
  return c.stage !== 'released' && (c.docStatus === 'submitted' || c.docStatus === 'verifying' || (c.docStatus === 'verified' && !c.verification));
}

export function readyForNote(c: CustodyCase): boolean {
  return c.approval === 'drafting' || c.approval === 'returned';
}

/** Verified & paid cases not yet placed in any e-file — can be combined into a new note sheet. */
export function availableForNote(c: CustodyCase): boolean {
  return readyForNote(c) && !c.noteFileId && c.docStatus === 'verified' && isPaid(c);
}

export const NOTE_FILE_LABEL: Record<import('../types/case').NoteFileStatus, Labeled> = {
  draft: { label: 'খসড়া', tone: 'gray' },
  pending: { label: 'অনুমোদন অপেক্ষমাণ', tone: 'warning' },
  approved: { label: 'চূড়ান্ত অনুমোদিত', tone: 'green' },
  returned: { label: 'সংশোধনের জন্য ফেরত', tone: 'danger' },
  rejected: { label: 'প্রত্যাখ্যাত', tone: 'danger' },
  held: { label: 'স্থগিত', tone: 'warning' },
};

export const paymentSourceLabel = (c: CustodyCase): Labeled =>
  c.paymentStatus === 'pending_verify' ? { label: 'দাবি — অযাচাইকৃত', tone: 'danger' }
  : c.payment.source === 'online' ? { label: 'অনলাইন গেটওয়ে (সিমুলেটেড)', tone: 'sky' }
  : c.payment.source === 'office' ? { label: 'অফিসে লিপিবদ্ধ', tone: 'navy' }
  : { label: '—', tone: 'gray' };

export function letterBlockers(c: CustodyCase): string[] {
  const b: string[] = [];
  if (c.docStatus !== 'verified') b.push('কাগজপত্র যাচাই সম্পন্ন হয়নি');
  if (!isPaid(c)) b.push('জরিমানা পরিশোধ নিশ্চিত হয়নি');
  if (c.approval !== 'approved') b.push('ঊর্ধ্বতন কর্তৃপক্ষের অনুমোদন সম্পন্ন হয়নি');
  return b;
}
